<script setup lang="ts">
import { computed, inject, onUnmounted, ref, Ref } from 'vue';
import { DateTime } from 'luxon';
import { formatDate } from '@/utils/dateUtils';
import { useAirPortionStore } from '@/store/AirPortionStore';
import { useComponentComposable } from '../useComponentComposable';
import Button from 'primevue/button';
import DataTable from 'primevue/datatable';
import RadioButton from 'primevue/radiobutton';
import TableColumn from 'primevue/column';

const dialogRef = inject('dialogRef') as Ref;
const isSave = dialogRef?.value?.options?.emits?.isSave;
const flightInformation = dialogRef?.value?.options?.data?.flightInformation;

const airPortionStore = useAirPortionStore();
const { displayDateTimeAlowLanguage } = useComponentComposable();
const selectOutbound = ref([]);
const selectInbound = ref([]);
const minDateInbound: any = ref([]);
const dateFormat = 'HH:mm (dd LLL)';

const formatAirportOutbound = () => {
  if (flightInformation) {
    const format = flightInformation.map((f) => {
      const a = f?.outbounds?.map((out) => {
        out.trip = `${out?.depAirport} ${out?.arrivalAirport}`;
        if (out?.stopFlights?.length) {
          out?.stopFlights?.map((sf) => {
            sf.sfTrip = `${sf.depAirport} ${sf.arrivalAirport}`;
            return sf;
          });
        }
        return out;
      });
      return a;
    });
    return format.toString();
  }
  return '';
};

const formatAirportInbound = () => {
  if (flightInformation) {
    const format = flightInformation.map((f) => {
      const a = f?.inbounds?.map((inb) => {
        inb.trips = `${inb?.depAirport} ${inb?.arrivalAirport}`;
        if (inb.stopFlights?.length) {
          inb?.stopFlights?.map((sf) => {
            sf.sfTrip = `${sf.depAirport} ${sf.arrivalAirport}`;
            return sf;
          });
        }
        return inb.trips;
      });
      return a;
    });
    return format.toString();
  }
  return '';
};

const formatAirportOneway = () => {
  if (flightInformation) {
    const format = flightInformation.map((f: any) => {
      const a = f?.oneWay?.map((one: any) => {
        one.trip = `${one?.depAirport} ${one?.arrivalAirport}`;
        if (one?.stopFlights?.length) {
          one?.stopFlights?.map((sf) => {
            sf.sfTrip = `${sf.depAirport} ${sf.arrivalAirport}`;
            return sf;
          });
        }
        return one;
      });
      return a;
    });
    return format.toString();
  }
  return '';
};

const countFlights = (flights: any) => {
  return flights.filter((flight: any) => flight).length;
};

const handleValidateSelectFlight = () => {
  const dataInbounds = flightInformation.filter((flight: any) => flight?.inbounds).length;
  const mixOption = countFlights(selectOutbound.value) === flightInformation.length && countFlights(selectInbound.value) === dataInbounds;

  return mixOption;
};

const save = () => {
  const combinedArrays = [];
  for (let i = 0; i < flightInformation.length; i++) {
    const pair = [selectOutbound.value[i], selectInbound.value[i]];
    combinedArrays.push(pair);
  }

  const combineFilteredArray = combinedArrays.map((item) => item.filter((el) => el !== undefined));
  isSave(selectInbound.value, selectOutbound.value, combineFilteredArray);
};

const isDisabledRequest = computed(() => {
  return !flightInformation?.length || !handleValidateSelectFlight();
});

const onSelectOutbound = (index: number, data: any) => {
  if (data.stopFlights) {
    const lastFlight = data?.stopFlights[data.stopFlights.length - 1];
    minDateInbound.value[index] = new Date(`${lastFlight.arrivalDate} ${lastFlight.arrivalTime}`);
  } else {
    minDateInbound.value[index] = new Date(`${data?.arrivalDate} ${data?.arrivalTime}`);
  }
};

const checkOutboundDate = (data: any, outbound: any, minDate: string) => {
  if (!outbound) {
    return true;
  }
  const outboundArrivalDate = DateTime.fromJSDate(minDate);
  const inboundDepartureDate = DateTime.fromJSDate(new Date(`${data.depDate} ${data.depTime}`));
  return outboundArrivalDate > inboundDepartureDate;
};

formatAirportOutbound();
formatAirportInbound();
formatAirportOneway();

onUnmounted(() => {
  airPortionStore.clearListFlightInformation();
});
</script>
<template>
  <div>
    <div v-if="!flightInformation?.length" class="flex justify-content-center pt-2 pb-4 font-bold text-lg">
      {{ $t('common.Unfortunately') }}
    </div>
    <div id="flight-information-content" v-else v-for="(option, index) in flightInformation" :key="index">
      <div class="mb-4">
        <span class="uppercase">{{ $t('common.option') }}</span> {{ index + 1 }} - {{ displayDateTimeAlowLanguage(option?.depDate) }}
        <span v-if="!option?.oneWay?.length">{{ $t('common.to_normal') }} {{ displayDateTimeAlowLanguage(option?.retDate) }}</span>
      </div>

      <div v-if="option?.outbounds?.length || option?.inbounds?.length" class="flex">
        <div class="item mr-2 outbound">
          <div>{{ $t('worksheet.outbound') }} - {{ displayDateTimeAlowLanguage(option?.depDate) }}</div>
          <DataTable :value="option?.outbounds" class="min-width-50rem" rowGroupMode="rowspan" groupRowsBy="index">
            <TableColumn field="index" header="" />
            <TableColumn class="w-3rem">
              <template #body="{ data }">
                <RadioButton v-model="selectOutbound[index]" name="selectOutbound" :value="data" @update:modelValue="onSelectOutbound(index, data)" />
              </template>
            </TableColumn>
            <TableColumn field="flight" :header="$t('advance_quote.form.flight_codes')">
              <template #body="{ data }">
                {{ data.flight }}
                <br />
                <div v-for="(item, indexI) in data.stopFlights" :key="indexI">
                  {{ item.flight }}
                </div>
              </template>
            </TableColumn>
            <TableColumn field="operated" :header="$t('advance_quote.form.operated')">
              <template #body="{ data }">
                {{ data.operated }}
                <br />
                <div v-for="(item, indexI) in data.stopFlights" :key="indexI">
                  {{ item.operated }}
                </div>
              </template>
            </TableColumn>
            <TableColumn field="trip" :header="$t('advance_quote.form.airports')">
              <template #body="{ data }">
                {{ data.trip }}
                <br />
                <div v-for="(item, indexI) in data.stopFlights" :key="indexI">
                  {{ item.sfTrip }}
                </div>
              </template>
            </TableColumn>
            <TableColumn field="depTime" :header="$t('advance_quote.form.leave')">
              <template #body="{ data }">
                {{ formatDate(`${data.depDate} ${data.depTime}`, dateFormat) }}
                <br />
                <div v-for="(item, indexI) in data.stopFlights" :key="indexI">
                  {{ formatDate(`${item.depDate} ${item.depTime}`, dateFormat) }}
                </div>
              </template>
            </TableColumn>
            <TableColumn field="dateTime" :header="$t('advance_quote.form.arrive')">
              <template #body="{ data }">
                {{ formatDate(`${data.arrivalDate} ${data.arrivalTime}`, dateFormat) }}
                <br />
                <div v-for="(item, indexI) in data.stopFlights" :key="indexI">
                  {{ formatDate(`${item.arrivalDate} ${item.arrivalTime}`, dateFormat) }}
                </div>
              </template>
            </TableColumn>
          </DataTable>
        </div>
        <div class="item inbound">
          <div>{{ $t('worksheet.inbound') }} - {{ displayDateTimeAlowLanguage(option?.retDate) }}</div>
          <DataTable :value="option?.inbounds" class="min-width-50rem">
            <TableColumn class="w-3rem">
              <template #body="{ data }">
                <RadioButton v-model="selectInbound[index]" name="selectInbound" :value="data" :disabled="checkOutboundDate(data, selectOutbound[index], minDateInbound[index])" />
              </template>
            </TableColumn>
            <TableColumn field="flight" :header="$t('advance_quote.form.flight_codes')">
              <template #body="{ data }">
                <div :class="checkOutboundDate(data, selectOutbound[index], minDateInbound[index]) && 'opacity-50'">
                  {{ data.flight }}
                  <br />
                  <div v-for="(item, indexI) in data.stopFlights" :key="indexI">
                    {{ item.flight }}
                  </div>
                </div>
              </template>
            </TableColumn>
            <TableColumn field="operated" :header="$t('advance_quote.form.operated')">
              <template #body="{ data }">
                <div :class="checkOutboundDate(data, selectOutbound[index], minDateInbound[index]) && 'opacity-50'">
                  {{ data.operated }}
                  <br />
                  <div v-for="(item, indexI) in data.stopFlights" :key="indexI">
                    {{ item.operated }}
                  </div>
                </div>
              </template>
            </TableColumn>
            <TableColumn field="trips" :header="$t('advance_quote.form.airports')">
              <template #body="{ data }">
                <div :class="checkOutboundDate(data, selectOutbound[index], minDateInbound[index]) && 'opacity-50'">
                  {{ data.trips }}
                  <br />
                  <div v-for="(item, indexI) in data.stopFlights" :key="indexI">
                    {{ item.sfTrip }}
                  </div>
                </div>
              </template>
            </TableColumn>
            <TableColumn field="depTime" :header="$t('advance_quote.form.leave')">
              <template #body="{ data }">
                <div :class="checkOutboundDate(data, selectOutbound[index], minDateInbound[index]) && 'opacity-50'">
                  {{ formatDate(`${data.depDate} ${data.depTime}`, dateFormat) }}
                  <br />
                  <div v-for="(item, indexI) in data.stopFlights" :key="indexI">
                    {{ formatDate(`${item.depDate} ${item.depTime}`, dateFormat) }}
                  </div>
                </div>
              </template>
            </TableColumn>
            <TableColumn field="dateTimes" :header="$t('advance_quote.form.arrive')">
              <template #body="{ data }">
                <div :class="checkOutboundDate(data, selectOutbound[index], minDateInbound[index]) && 'opacity-50'">
                  {{ formatDate(`${data.arrivalDate} ${data.arrivalTime}`, dateFormat) }}
                  <br />
                  <div v-for="(item, indexI) in data.stopFlights" :key="indexI">
                    {{ formatDate(`${item.arrivalDate} ${item.arrivalTime}`, dateFormat) }}
                  </div>
                </div>
              </template>
            </TableColumn>
          </DataTable>
        </div>
      </div>

      <div v-if="option?.oneWay?.length" class="flex">
        <div class="item mr-2 oneWay">
          <div>{{ $t('worksheet.one_way') }} - {{ displayDateTimeAlowLanguage(option?.depDate) }}</div>
          <DataTable :value="option?.oneWay" class="min-width-50rem" rowGroupMode="rowspan" groupRowsBy="index">
            <TableColumn field="index" header="" />
            <TableColumn class="w-3rem">
              <template #body="{ data }">
                <RadioButton v-model="selectOutbound[index]" name="selectOneway" :value="data" />
              </template>
            </TableColumn>
            <TableColumn field="flight" :header="$t('advance_quote.form.flight_codes')">
              <template #body="{ data }">
                {{ data.flight }}
                <br />
                <div v-for="(item, indexI) in data?.stopFlights" :key="indexI">
                  {{ item.flight }}
                </div>
              </template>
            </TableColumn>
            <TableColumn field="operated" :header="$t('advance_quote.form.operated')">
              <template #body="{ data }">
                {{ data.operated }}
                <br />
                <div v-for="(item, indexI) in data?.stopFlights" :key="indexI">
                  {{ item.operated }}
                </div>
              </template>
            </TableColumn>
            <TableColumn field="trip" :header="$t('advance_quote.form.airports')">
              <template #body="{ data }">
                {{ data?.trip }}
                <br />
                <div v-for="(item, indexI) in data?.stopFlights" :key="indexI">
                  {{ item.sfTrip }}
                </div>
              </template>
            </TableColumn>
            <TableColumn field="depTime" :header="$t('advance_quote.form.leave')">
              <template #body="{ data }">
                {{ formatDate(`${data.depDate} ${data.depTime}`, dateFormat) }}
                <br />
                <div v-for="(item, indexI) in data?.stopFlights" :key="indexI">
                  {{ formatDate(`${item.depDate} ${item.depTime}`, dateFormat) }}
                </div>
              </template>
            </TableColumn>
            <TableColumn field="arrivalTime" :header="$t('advance_quote.form.arrive')">
              <template #body="{ data }">
                {{ formatDate(`${data.arrivalDate} ${data.arrivalTime}`, dateFormat) }}
                <br />
                <div v-for="(item, indexI) in data?.stopFlights" :key="indexI">
                  {{ formatDate(`${item.arrivalDate} ${item.arrivalTime}`, dateFormat) }}
                </div>
              </template>
            </TableColumn>
          </DataTable>
        </div>
      </div>
    </div>
    <div class="request">
      <Button icon="pi pi-check" @click="save()" :label="$t('common.request')" :disabled="isDisabledRequest" />
    </div>
  </div>
</template>
<style lang="scss">
.min-width-50rem {
  min-width: 50rem;
}
.item {
  background: $col-white;
  border: 1px solid $col-whiteblu;
  padding: 2rem;
  margin-bottom: 2rem;
  box-shadow: $card-shadow;
  border-radius: 6px;
}

.request {
  display: flex;
  justify-content: flex-end;
}

.p-datatable .p-datatable-tbody > tr.p-highlight {
  background-color: transparent !important;
  color: inherit !important;
}
</style>
